import React from 'react';
import { FieldDefinition } from './FieldRenderer';

interface DynamicFileFieldProps {
  field: FieldDefinition;
  value: any;
  onChange: (fieldKey: string, value: any) => void;
  error?: string;
  disabled?: boolean;
  isArabic: boolean;
}

const DynamicFileField: React.FC<DynamicFileFieldProps> = ({
  field,
  value,
  onChange,
  error,
  disabled,
  isArabic
}) => {
  const label = isArabic ? field.label_ar || field.label : field.label;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    onChange(field.key, file);
  };

  const getAcceptedFormats = () => {
    if (field.validation?.accepted_formats) {
      return field.validation.accepted_formats.join(',');
    }
    if (field.validation?.accept) {
      return field.validation.accept;
    }
    return '.jpeg,.jpg,.png,.pdf,.webp';
  };

  const getMaxFileSize = () => {
    if (field.validation?.max_size_mb) {
      return field.validation.max_size_mb;
    }
    return 5; // Default 5MB
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
        {field.required && <span className="text-red-500 ml-1">*</span>}
        <span className="text-gray-500 text-xs ml-2">
          ({isArabic ? 'الحد الأقصى' : 'Max'} {getMaxFileSize()}MB)
        </span>
      </label>
      <input
        type="file"
        onChange={handleChange}
        accept={getAcceptedFormats()}
        disabled={disabled}
        required={field.required}
        className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          error ? 'border-red-500' : 'border-gray-300'
        } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
      />
      {value && value instanceof File && (
        <div className="mt-2 text-sm text-gray-600">
          <span className="font-medium">{isArabic ? 'الملف المحدد:' : 'Selected file:'}</span> {value.name} ({formatFileSize(value.size)})
        </div>
      )}
      <div className="mt-1 text-xs text-gray-500">
        {isArabic ? 'أنواع الملفات المقبولة:' : 'Accepted file types:'} {getAcceptedFormats()}
      </div>
      {error && (
        <p className="text-red-500 text-sm mt-1">{error}</p>
      )}
    </div>
  );
};

export default DynamicFileField;